import { c as _c } from "react/compiler-runtime";
import { ProgressBar } from '@systemeio/components';
import { NotificationItemCancelButton, NotificationItemView, type NotificationItemVariant } from 'modules/notifications/components/notification-item-view';
import { getJobNotificationIcon } from 'modules/notifications/constants/notification-icons';
import { useRevalidateNotificationsOnJobEnd } from 'modules/notifications/hooks/use-revalidate-notifications-on-job-end';
import { SharingFailReasonEnum } from 'modules/share/enum/sharing-fail-reason-enum';
import { SharingStatusEnum } from 'modules/share/enum/sharing-status-enum';
import { useCancelSharing } from 'modules/share/hooks/use-cancel-sharing';
import { useSharingStatusPolling } from 'modules/share/hooks/use-sharing-status-polling';
import type { NotificationSharingItemInterface } from 'modules/share/types/sharing-notification-interface';
import { type ActionMenuItemProps } from 'shared/components';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { getWorkflowEditorUrl } from 'shared/utils/get-workflow-editor-url';
export interface SharingNotificationProps {
  actions?: ActionMenuItemProps[];
  notification: NotificationSharingItemInterface;
  variant?: NotificationItemVariant;
  onNavigate?: () => void;
}
const SharingNotificationItem = t0 => {
  const $ = _c(9);
  const {
    actions,
    notification: notificationItem,
    variant,
    onNavigate
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    cancelSharing,
    isCancelling
  } = useCancelSharing();
  const t1 = notificationItem.data.status === SharingStatusEnum.Running;
  let t2;
  if ($[0] !== notificationItem.data.sharingId || $[1] !== t1) {
    t2 = {
      sharingId: notificationItem.data.sharingId,
      allowFetch: t1
    };
    $[0] = notificationItem.data.sharingId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    sharingStatus,
    mutate
  } = useSharingStatusPolling(t2);
  const notification = {
    ...notificationItem,
    data: {
      ...notificationItem.data,
      ...(sharingStatus ?? {})
    }
  };
  const isRunning = notification.data.status === SharingStatusEnum.Running;
  const sharedResourceId = notification.data.sharedResourceId;
  useRevalidateNotificationsOnJobEnd(sharingStatus !== undefined && sharingStatus.status !== SharingStatusEnum.Running);
  const handleNotificationClick = notification.data.status === SharingStatusEnum.Success && sharedResourceId ? () => {
    window.open(getWorkflowEditorUrl(sharedResourceId), "_blank");
    onNavigate?.();
  } : undefined;
  const handleCancel = async () => {
    await cancelSharing(notification.data.sharingId);
    await mutate(_temp, false);
  };
  const getMessage = () => {
    switch (notification.data.status) {
      case SharingStatusEnum.Running:
        {
          return t("dashboard.notifications.workflow_sharing_in_progress");
        }
      case SharingStatusEnum.Failed:
        {
          switch (notification.data.failReason) {
            case SharingFailReasonEnum.CancelledByUser:
              {
                return t("dashboard.notifications.workflow_sharing_cancelled");
              }
            case SharingFailReasonEnum.FailedToShare:
              {
                return t("dashboard.notifications.workflow_sharing_failed_to_share");
              }
            case SharingFailReasonEnum.ProcessingTimeExceeded:
              {
                return t("dashboard.notifications.workflow_sharing_processing_time_exceeded");
              }
            case SharingFailReasonEnum.ResourceRemoved:
              {
                return t("dashboard.notifications.workflow_sharing_resource_removed");
              }
            default:
              {
                return t("dashboard.notifications.workflow_sharing_failed");
              }
          }
        }
      case SharingStatusEnum.Success:
        {
          return t("dashboard.notifications.workflow_sharing_completed");
        }
    }
  };
  const t3 = notification.read;
  const t4 = getJobNotificationIcon(notification.data.failReason === SharingFailReasonEnum.CancelledByUser ? "cancelled" : notification.data.status);
  let t5;
  if ($[3] !== getMessage) {
    t5 = getMessage();
    $[3] = getMessage;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== handleCancel || $[6] !== isCancelling || $[7] !== isRunning) {
    t6 = isRunning && <NotificationItemCancelButton isLoading={isCancelling} onClick={handleCancel} />;
    $[5] = handleCancel;
    $[6] = isCancelling;
    $[7] = isRunning;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return <NotificationItemView variant={variant} isRead={t3} actions={actions} icon={t4} message={t5} time={isRunning ? notification.createdAt : notification.data.updatedAt} onClick={handleNotificationClick} controls={t6}>{isRunning && notification.data.progressPercent !== undefined && <ProgressBar labelPosition="side" value={notification.data.progressPercent} />}</NotificationItemView>;
};
export default SharingNotificationItem;
function _temp(data) {
  if (!data) {
    return;
  }
  return {
    ...data,
    status: SharingStatusEnum.Failed,
    failReason: SharingFailReasonEnum.CancelledByUser
  };
}